import { matchPath, useLocation } from "react-router";

import { ROUTES } from "@/constants/routes";

interface PageMeta {
	description?: string;
	title: string;
}

/**
 * Route → page header copy. Detail routes share a parent title; the page
 * body still carries the specific entity context.
 */
const PAGE_META: { meta: PageMeta; path: string }[] = [
	{
		meta: {
			description:
				"Configure the selected organization's locations, zones, and sensors.",
			title: "Location Management",
		},
		path: ROUTES.ORGANIZATION_LOCATIONS,
	},
	{
		meta: {
			description: "Configure one location's zones and sensors.",
			title: "Location",
		},
		path: ROUTES.ORGANIZATION_LOCATION,
	},
	{
		meta: {
			description: "Lookout installations for the selected organization.",
			title: "Lookout Installations",
		},
		path: ROUTES.ORGANIZATION_INSTALLATIONS,
	},
	{
		meta: {
			description: "Rollout switch for plausibility verification.",
			title: "Plausibility settings",
		},
		path: ROUTES.ORGANIZATION_PLAUSIBILITY_SETTINGS,
	},
	{
		meta: {
			description: "Organization-scoped prior probabilities.",
			title: "Scoped priors",
		},
		path: ROUTES.ORGANIZATION_PRIORS,
	},
	{
		meta: {
			description: "Per-camera feature overrides for this organization.",
			title: "Camera features",
		},
		path: ROUTES.ORGANIZATION_CAMERA_FEATURES,
	},
	{
		meta: {
			description: "What the verifier did with each alert-worthy detection.",
			title: "Verifications",
		},
		path: ROUTES.ORGANIZATION_VERIFICATIONS,
	},
	{
		meta: {
			description: "Review one verification and record a verdict.",
			title: "Verification",
		},
		path: ROUTES.ORGANIZATION_VERIFICATION,
	},
	{
		meta: {
			description: "Draft and review scene facts for cameras.",
			title: "Scene proposals",
		},
		path: ROUTES.ORGANIZATION_SCENE_PROPOSALS,
	},
	{
		meta: {
			description: "Review a scene-fact proposal before it reaches a camera.",
			title: "Scene proposal",
		},
		path: ROUTES.ORGANIZATION_SCENE_PROPOSAL,
	},
	{
		meta: {
			description:
				"Find the organization's events and add their clips to the gold standard set.",
			title: "Events",
		},
		path: ROUTES.ORGANIZATION_EVENTS,
	},
	{
		meta: {
			description:
				"An event's clips, to add to the gold standard set, and its cross-camera tracks.",
			title: "Event",
		},
		path: ROUTES.ORGANIZATION_EVENT,
	},
	{
		meta: {
			description:
				"One person followed across the site's cameras from an event: where they came from and where they went.",
			title: "Track",
		},
		path: ROUTES.ORGANIZATION_TRACK,
	},
	{
		meta: {
			description: "Corrections to events from either app.",
			title: "Reanalysis",
		},
		path: ROUTES.ORGANIZATION_REANALYSIS,
	},
	{
		meta: {
			description: "Correct an event and review its reanalysis history.",
			title: "Reanalysis event",
		},
		path: ROUTES.ORGANIZATION_REANALYSIS_EVENT,
	},
	{
		meta: {
			description: "Replay configuration changes against stored verifications.",
			title: "Replay",
		},
		path: ROUTES.ORGANIZATION_REPLAY,
	},
	{
		meta: {
			description: "VLM spend for the selected organization.",
			title: "Costs",
		},
		path: ROUTES.ORGANIZATION_COSTS,
	},
	{
		meta: {
			description:
				"Alerts no alertable class explained; create the class or ignore.",
			title: "Unclassified alerts",
		},
		path: ROUTES.GLOBAL_UNCLASSIFIED,
	},
	{
		meta: {
			description:
				"Classes, descriptions, and verifier features shared by every organization.",
			title: "Taxonomy",
		},
		path: ROUTES.GLOBAL_TAXONOMY,
	},
	{
		meta: {
			description: "Global prior probabilities shared across organizations.",
			title: "Global priors",
		},
		path: ROUTES.GLOBAL_PRIORS,
	},
	{
		meta: {
			description: "Calibration proposals computed from recorded verdicts.",
			title: "Calibration",
		},
		path: ROUTES.GLOBAL_CALIBRATION,
	},
	{
		meta: {
			description:
				"Gold standard clips with hand-written ground truth, shared with the internal tool.",
			title: "Videos",
		},
		path: ROUTES.GLOBAL_GOLD_VIDEOS,
	},
	{
		meta: {
			description:
				"Ask a gold standard video a question; RF-DETR finds the objects and Gemini answers.",
			title: "Vision lab",
		},
		path: ROUTES.GLOBAL_VISION_LAB,
	},
];

/** Resolve the sticky page header for the current route. */
export const usePageMeta = (): PageMeta => {
	const { pathname } = useLocation();

	const match = PAGE_META.find(
		({ path }) => path && matchPath({ end: true, path }, pathname),
	);

	return match?.meta ?? { title: "Shadow Realm" };
};
